import type { PostHog } from "posthog-js";

import { defaultConfig } from "./config";
import { formatProperty } from "./utils/formatProperty";
import { flattenObject } from "./utils/flattenObject";
import { overrideTimestamp } from "./utils/overrideTimestamp";
import { composeEnhancedEvent } from "./utils/composeEnhancedEvent";
import { ViewportObserver } from "./observers/ViewportObserver/ViewportObserver";
import { NavigationObserver } from "./observers/NavigationObserver/NavigationObserver";
import { pageViewEventBuilder } from "./events/pageView/pageViewEventBuilder";
import { pageLeaveEventBuilder } from "./events/pageLeave/pageLeaveEventBuilder";
import { elementViewEventBuilder } from "./events/elementView/elementViewEventBuilder";
import { elementClickEventBuilder } from "./events/elementClick/elementClickEventBuilder";
import { elementSelectEventBuilder } from "./events/elementSelect/elementSelectEventBuilder";

import type {
  WebTrackerOptions,
  Identify,
  Reset,
  CapturePageView,
  CapturePageLeave,
  CaptureElementView,
  CaptureElementClick,
  CaptureElementSelect,
  CaptureEvent,
} from "./types";

/**
 * Web tracking wrapper for PostHog analytics.
 */
class WebTracker {
  private readonly internalTracker: PostHog;
  private readonly featureEnabled: boolean;

  private readonly viewportObserver: ViewportObserver;
  private readonly navigationObserver: NavigationObserver;

  private readonly elementViewNameAttribute = "data-element-name";
  private readonly elementViewCustomAttribute = "data-custom-properties";

  /**
   * Creates a new WebTracker instance. Initializes PostHog if feature is enabled.
   * @param internalTracker - PostHog instance
   * @param options - Configuration options
   */
  constructor(internalTracker: PostHog, options: WebTrackerOptions) {
    const { apiKey, apiHost, featureEnabled, customConfig } = options;

    this.internalTracker = internalTracker;
    this.featureEnabled = featureEnabled && !!apiKey && !!apiHost;

    this.navigationObserver = new NavigationObserver();

    this.viewportObserver = new ViewportObserver(
      this.elementViewNameAttribute,
      this.elementViewCustomAttribute
    );

    if (this.featureEnabled) {
      this.internalTracker.init(apiKey, {
        api_host: apiHost,
        ...defaultConfig,
        ...customConfig,
      });
    }
  }

  /**
   * Identifies a user with agent properties.
   * @param args - AgentId and properties
   * @returns void
   */
  public identify: Identify = ({ agentId, properties }) => {
    if (!this.featureEnabled) return;

    this.internalTracker.identify(agentId, {
      agent_id: formatProperty(properties.agentId),
      agent_email: formatProperty(properties.agentEmail),
      agent_date_of_birth: formatProperty(properties.agentDateOfBirth),
      agency_id: formatProperty(properties.agencyId),
      agency_name: formatProperty(properties.agencyName),
      agency_type: formatProperty(properties.agencyType),
      wholesaler_id: formatProperty(properties.wholesalerId),
    });
  };

  /**
   * Resets user identification and optionally device ID.
   * @param args - Reset configuration options
   * @returns void
   */
  public reset: Reset = ({ resetDevice } = { resetDevice: false }) => {
    if (!this.featureEnabled) return;

    this.internalTracker.reset(resetDevice);
  };

  /**
   * Captures a PageView event.
   * @param input - PageView event input
   * @returns void
   */
  public capturePageView: CapturePageView = (input) => {
    if (!this.featureEnabled) return;

    const pageViewEvent = pageViewEventBuilder(input);

    this.captureEvent(pageViewEvent);
  };

  /**
   * Captures a PageLeave event.
   * @param input - PageLeave event input
   * @returns void
   */
  public capturePageLeave: CapturePageLeave = (input) => {
    if (!this.featureEnabled) return;

    const pageLeaveEvent = pageLeaveEventBuilder(input);

    this.captureEvent(pageLeaveEvent);
  };

  /**
   * Captures an ElementView event.
   * @param input - ElementView event input
   * @returns void
   */
  public captureElementView: CaptureElementView = (input) => {
    if (!this.featureEnabled) return;

    const elementViewEvent = elementViewEventBuilder(input);

    this.captureEvent(elementViewEvent);
  };

  /**
   * Captures an ElementClick event.
   * @param input - ElementClick event input
   * @returns void
   */
  public captureElementClick: CaptureElementClick = (input) => {
    if (!this.featureEnabled) return;

    const elementClickEvent = elementClickEventBuilder(input);

    this.captureEvent(elementClickEvent);
  };

  /**
   * Captures an ElementSelect event.
   * @param input - ElementSelect event input
   * @returns void
   */
  public captureElementSelect: CaptureElementSelect = (input) => {
    if (!this.featureEnabled) return;

    const elementSelectEvent = elementSelectEventBuilder(input);

    this.captureEvent(elementSelectEvent);
  };

  /**
   * Automatically captures ElementView events when elements enter the viewport.
   * @returns void
   */
  public createElementViewTracker = (): void => {
    if (!this.featureEnabled) return;

    this.viewportObserver.createObserver((elementName, customProperties) => {
      this.captureElementView({
        elementName,
        customProperties,
      });
    });
  };

  /**
   * Destroys the viewport tracking observer and cleans up resources.
   * @returns void
   */
  public destroyElementViewTracker = (): void => {
    if (!this.featureEnabled) return;

    this.viewportObserver.destroyObserver();
  };

  /**
   * Automatically captures PageView events when entering new pages.
   * @returns void
   */
  public createPageViewTracker = (): void => {
    if (!this.featureEnabled) return;

    this.navigationObserver.createPageViewObserver((currentPath) => {
      this.capturePageView({ currentPath });
    });
  };

  /**
   * Destroys the navigation tracking observer and cleans up resources.
   * @returns void
   */
  public destroyPageViewTracker = (): void => {
    if (!this.featureEnabled) return;

    this.navigationObserver.destroyPageViewObserver();
  };

  /**
   * Automatically captures PageLeave events when leaving pages.
   * @returns void
   */
  public createPageLeaveTracker = (): void => {
    if (!this.featureEnabled) return;

    this.navigationObserver.createPageLeaveObserver((currentPath) => {
      this.capturePageLeave({ currentPath });
    });
  };

  /**
   * Destroys the navigation tracking observer and cleans up resources.
   * @returns void
   */
  public destroyPageLeaveTracker = (): void => {
    if (!this.featureEnabled) return;

    this.navigationObserver.destroyPageLeaveObserver();
  };

  /**
   * Captures an event.
   * @param event - BaseEvent to capture
   * @returns void
   */
  private captureEvent: CaptureEvent = (event) => {
    if (!this.featureEnabled) return;

    const enhancedEvent = composeEnhancedEvent(event);

    this.internalTracker.capture(
      enhancedEvent.name,
      flattenObject(enhancedEvent),
      overrideTimestamp(enhancedEvent)
    );
  };
}

export { WebTracker };
